/**
 * Error reporting surface for RouteFlow.
 *
 * Why this file replaces the previous Sentry-only wrapper:
 *
 *   1. `@sentry/react` isn't installed in the repo. The old import was
 *      causing a TypeScript error and would fail the build the moment
 *      Vite tried to resolve it.
 *
 *   2. The previous module had a duplicate `beforeSend` (the second one
 *      silently overrode the first, so dev-mode filtering never ran), and
 *      called the pre-v8 Sentry constructor API which the current SDK
 *      replaced with integration *functions*.
 *
 * Design:
 *
 *   - `reportError(err, context?)` is the only public reporting surface.
 *     Both error boundaries and the global window.onerror handler go through
 *     it. Internally it:
 *       (a) console.error always, so dev tools stay useful;
 *       (b) writes to a Supabase `client_errors` table (rate-limited per
 *           session to avoid hot loops);
 *       (c) forwards to Sentry IF a Sentry-compatible global is available.
 *           That makes it cheap to opt back into Sentry later — just install
 *           `@sentry/react` and wire `window.Sentry = Sentry` in main.tsx.
 *
 *   - `setReporterUser(user)` stamps user context so writes include
 *     who hit the error. Called from useAuth after sign-in.
 *
 *   - `SentryErrorBoundary` exists for API parity with the previous main.tsx
 *     code path; it's a thin class boundary that forwards into reportError.
 *
 *   - Initialisation is `initReporter()`, called once from main.tsx. It
 *     wires `window.onerror` and `unhandledrejection`, plus picks up the
 *     current Supabase user if one is already in storage.
 */

import { supabase } from "@/integrations/supabase/client";

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

interface ReporterUser {
  id?:    string;
  email?: string;
}

interface ReporterContext {
  tags?:    Record<string, string>;
  extra?:   Record<string, unknown>;
  source?:  "route_error_boundary" | "app_error_boundary" | "window_onerror"
          | "unhandled_rejection" | "manual";
  routeName?: string;
}

interface SentryLike {
  captureException?: (err: unknown, opts?: unknown) => void;
  setUser?:          (user: ReporterUser | null) => void;
}

let currentUser: ReporterUser | null = null;

// Per-session caps so a render loop that throws on every frame doesn't DOS
// our own `client_errors` table.
const MAX_REPORTS_PER_SESSION = 50;
const MIN_GAP_MS              = 250; // throttle: at most 4/s

let reportsThisSession = 0;
let lastReportAt       = 0;

// Drop repeated identical errors — only the first of N identical fingerprints
// makes it to the table per session.
const seenFingerprints = new Set<string>();
const MAX_FINGERPRINTS = 200;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getSentry(): SentryLike | null {
  if (typeof window === "undefined") return null;
  const g = window as unknown as { Sentry?: SentryLike };
  return g.Sentry ?? null;
}

function fingerprintOf(err: unknown): string {
  if (err instanceof Error) {
    return `${err.name}|${err.message}|${(err.stack ?? "").split("\n").slice(0, 3).join("|")}`;
  }
  try { return String(err); } catch { return "unknown"; }
}

function scrub(value: unknown): unknown {
  // Strip obvious secret-looking values from the extra object so we don't
  // accidentally write a Bearer token into the errors table.
  if (!value || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(scrub);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (/token|secret|password|api_key|authorization/i.test(k)) out[k] = "[scrubbed]";
    else out[k] = scrub(v);
  }
  return out;
}

function pickSupabaseUser(): ReporterUser | null {
  if (typeof localStorage === "undefined") return null;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith("sb-") || !k.endsWith("-auth-token")) continue;
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as { user?: ReporterUser };
      if (parsed?.user?.id) return { id: parsed.user.id, email: parsed.user.email };
    }
  } catch { /* ignore */ }
  return null;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function setReporterUser(user: ReporterUser | null): void {
  currentUser = user;
  const sentry = getSentry();
  if (sentry?.setUser) {
    try { sentry.setUser(user); } catch { /* ignore */ }
  }
}

export function reportError(err: unknown, context: ReporterContext = {}): void {
  // 1. console always.
   
  console.error(`[reportError:${context.source ?? "manual"}]`, err, context);

  // 2. throttle + cap.
  const now = Date.now();
  if (reportsThisSession >= MAX_REPORTS_PER_SESSION) return;
  if (now - lastReportAt < MIN_GAP_MS) return;
  lastReportAt = now;
  reportsThisSession++;

  // 3. dedup by fingerprint.
  const fp = fingerprintOf(err);
  if (seenFingerprints.has(fp)) return;
  if (seenFingerprints.size > MAX_FINGERPRINTS) seenFingerprints.clear();
  seenFingerprints.add(fp);

  // 4. forward to Sentry if available.
  const sentry = getSentry();
  if (sentry?.captureException) {
    try {
      sentry.captureException(err, {
        tags:  context.tags,
        extra: scrub(context.extra) as Record<string, unknown> | undefined,
        user:  currentUser ?? undefined,
      });
    } catch { /* ignore */ }
  }

  // 5. persist to Supabase client_errors.
  const user = currentUser ?? pickSupabaseUser();
  const payload = {
    fingerprint: fp,
    message:     err instanceof Error ? err.message : String(err),
    name:        err instanceof Error ? err.name    : "Unknown",
    stack:       err instanceof Error ? err.stack ?? null : null,
    source:      context.source ?? "manual",
    route_name:  context.routeName ?? null,
    user_id:     user?.id ?? null,
    user_email:  user?.email ?? null,
    url:         typeof window !== "undefined" ? window.location.pathname : null,
    user_agent:  typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 256) : null,
    tags:        context.tags ?? null,
    extra:       scrub(context.extra) as Record<string, unknown> | null,
  };

  // Fire-and-forget. We deliberately ignore the result — a failure to report
  // is a worse-but-acceptable outcome compared to surfacing it to the user.
  try {
    void supabase.from("client_errors").insert(payload).then(() => undefined);
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// initReporter — wire global error sources once
// ---------------------------------------------------------------------------

let initialised = false;

export function initReporter(): void {
  if (initialised) return;
  initialised = true;

  if (typeof window === "undefined") return;

  // Pick up the user from storage on boot, before useAuth has a chance to
  // run. We'll overwrite it from useAuth on the first auth event.
  const u = pickSupabaseUser();
  if (u) currentUser = u;

  window.addEventListener("error", (ev: ErrorEvent) => {
    reportError(ev.error ?? new Error(ev.message), {
      source: "window_onerror",
      extra: { filename: ev.filename, line: ev.lineno, col: ev.colno },
    });
  });

  window.addEventListener("unhandledrejection", (ev: PromiseRejectionEvent) => {
    const reason = ev.reason;
    const err = reason instanceof Error ? reason
              : typeof reason === "string" ? new Error(reason)
              : new Error("Unhandled promise rejection");
    reportError(err, { source: "unhandled_rejection", extra: { reason: scrub(reason) } });
  });
}

// ---------------------------------------------------------------------------
// SentryErrorBoundary — passthrough class boundary for API parity
// ---------------------------------------------------------------------------

import { Component, type ReactNode } from "react";

interface BoundaryProps {
  children:  ReactNode;
  fallback?: ReactNode | ((args: { error: Error; resetError: () => void }) => ReactNode);
}

export class SentryErrorBoundary extends Component<BoundaryProps, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) { return { error }; }

  componentDidCatch(error: Error) {
    reportError(error, { source: "manual", tags: { boundary: "Sentry" } });
  }

  resetError = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      const fb = this.props.fallback;
      if (typeof fb === "function") return fb({ error: this.state.error, resetError: this.resetError });
      if (fb) return fb;
      return null;
    }
    return this.props.children;
  }
}

// Legacy export kept so any code that still imports `Sentry` doesn't break.
export const Sentry = { ErrorBoundary: SentryErrorBoundary };
